import React, { useState, useEffect, useMemo } from 'react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { getCalculatorData } from '../../data/calculatorData';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../../common/SafeIcon';
import MediaLibrary from './MediaLibrary';

const { LuX, LuSave, LuImages, LuCheckCircle, LuClock, LuSparkles, LuWrench, LuTrash2, LuCalculator, LuZap, LuChevronDown, LuTag, LuRefreshCw, LuHeartHandshake, LuUsers, LuVideo, LuLayers, LuPlus } = LuIcons;

const DEFAULT_WHATSAPP = '213666667878';

const initialForm = {
  brand: '',
  model: '',
  year: new Date().getFullYear(),
  price: '',
  mileage: '',
  description: '',
  video_url: '', 
  whatsapp: DEFAULT_WHATSAPP,
  is_featured: false,
  status: 'available',
  condition: 'used',
  transmission: 'manual',
  color: '',
  shipping_price: '',
  customs_price: '',
  total_price: '',
  commission: '',
  parent_commission: '',
  discount_enabled: false,
  discount_price: '',
  discount_end_date: '',
  discount_auto_repeat: false,
  discount_repeat_days: 7,
};

const PriceInput = ({ label, name, value, onChange, hint, highlight, colorClass = 'emerald' }) => (
  <div className={`p-4 rounded-xl border-2 ${highlight ? `border-${colorClass}-400 bg-${colorClass}-50` : 'border-slate-200 bg-white'}`}>
    <label className={`block text-sm font-bold mb-1.5 ${highlight ? `text-${colorClass}-700` : 'text-slate-700'}`}>{label}</label>
    {hint && <p className="text-xs text-slate-400 mb-2">{hint}</p>}
    <div className="relative">
      <input type="number" name={name} value={value} onChange={onChange} placeholder="0" className={`w-full p-3 pe-12 border rounded-xl focus:ring-2 outline-none transition-all ${highlight ? `border-${colorClass}-300 focus:ring-${colorClass}-400 bg-white font-black text-${colorClass}-700 text-lg` : 'border-slate-200 focus:ring-emerald-500'}`} />
      <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold ${highlight ? `text-${colorClass}-500` : 'text-slate-400'}`}>دج</span>
    </div>
  </div>
);

const CustomsAutoFill = ({ brand, model, year, condition, onSelect }) => {
  const [open, setOpen] = useState(false);
  const calcData = useMemo(() => getCalculatorData(), []);
  const matches = useMemo(() => {
    if (!brand) return [];
    return calcData.filter(item => item.brand === brand && (!model || item.model === model));
  }, [calcData, brand, model]);

  if (matches.length === 0) return null;

  const currentYear = new Date().getFullYear();
  const age = currentYear - Number(year);

  const getAgePrice = (item) => {
    if (condition === 'new') return { price: item.neuf, label: 'جديدة' };
    if (age <= 1) return { price: item.m1, label: 'أقل من سنة' };
    if (age <= 2) return { price: item.m2, label: 'أقل من سنتين' };
    return { price: item.m3, label: 'أقل من 3 سنوات' };
  };

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(o => !o)} className="flex items-center gap-2 w-full px-4 py-3 bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 hover:border-blue-400 text-blue-700 rounded-xl font-bold text-sm transition-all">
        <SafeIcon icon={LuZap} className="w-4 h-4 text-blue-500" /> تعبئة سعر الجمركة تلقائياً
        <SafeIcon icon={LuChevronDown} className={`w-4 h-4 ms-auto transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-2 right-0 left-0 bg-white border border-slate-200 rounded-2xl shadow-xl z-20 overflow-hidden max-h-72 overflow-y-auto">
          {matches.map((item, i) => {
            const { price, label } = getAgePrice(item);
            const isDsl = item.energy === 'DSL' || price === 0;
            return (
              <button key={i} type="button" disabled={isDsl} onClick={() => {
                if (!isDsl) {
                  onSelect(Math.round(price * 1000000));
                  setOpen(false);
                }
              }} className={`w-full flex items-center justify-between px-4 py-3 border-b border-slate-50 text-right transition-colors ${isDsl ? 'opacity-40 cursor-not-allowed bg-slate-50' : 'hover:bg-blue-50 cursor-pointer'}`}>
                <div className="text-right">
                  <p className="text-sm font-bold text-slate-800">{item.brand} {item.model}</p>
                  <p className="text-xs text-slate-500">{item.energy} • {item.engine}cc • {item.origin === 'Europe' ? '🇪🇺' : '🌍'} • {label}</p>
                </div>
                <div className="text-right ms-3">
                  {isDsl ? <span className="text-red-500 text-xs font-bold">غير مسموح</span> : <span className="text-blue-700 font-black text-sm">{price.toLocaleString()} مليون</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// --- Variant Management Component ---
const VariantManager = ({ variants, setVariants, baseData }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newVariant, setNewVariant] = useState({
    color: '',
    transmission: 'automatic',
    price: baseData.price || '',
    customs_price: baseData.customs_price || '',
    shipping_price: baseData.shipping_price || '',
    total_price: baseData.total_price || '',
    commission: baseData.commission || '',
    parent_commission: baseData.parent_commission || ''
  });

  const handleAdd = () => {
    const v = {
      ...newVariant,
      id: Date.now().toString(),
      price: Number(newVariant.price) || 0,
      customs_price: Number(newVariant.customs_price) || 0,
      shipping_price: Number(newVariant.shipping_price) || 0,
      total_price: Number(newVariant.total_price) || (Number(newVariant.price) + Number(newVariant.customs_price) + Number(newVariant.shipping_price)),
      commission: Number(newVariant.commission) || 0,
      parent_commission: Number(newVariant.parent_commission) || 0,
    };
    setVariants([...variants, v]);
    setIsAdding(false);
    setNewVariant({
      color: '', transmission: 'automatic',
      price: baseData.price || '', customs_price: baseData.customs_price || '', shipping_price: baseData.shipping_price || '',
      total_price: '', commission: baseData.commission || '', parent_commission: baseData.parent_commission || ''
    });
  };

  const removeVariant = (id) => setVariants(variants.filter(v => v.id !== id));

  return (
    <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 mt-6">
      <div className="flex items-center justify-between mb-4">
        <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <SafeIcon icon={LuLayers} className="w-5 h-5 text-indigo-600" />
          نسخ السيارة (ألوان وناقل حركة إضافي)
        </label>
        {!isAdding && (
          <button type="button" onClick={() => setIsAdding(true)} className="text-xs bg-indigo-100 text-indigo-700 hover:bg-indigo-200 px-3 py-1.5 rounded-lg font-bold flex items-center gap-1">
            <SafeIcon icon={LuPlus} className="w-3.5 h-3.5" /> إضافة نسخة
          </button>
        )}
      </div>

      <p className="text-xs text-slate-500 mb-4">إذا كانت السيارة تتوفر بعدة نسخ (مثال: لون مختلف بسعر مختلف، أو ناقل حركة أوتوماتيك بدلاً من يدوي)، يمكنك إضافتها هنا.</p>

      {variants.length > 0 && (
        <div className="space-y-2 mb-4">
          {variants.map(v => (
            <div key={v.id} className="flex flex-wrap items-center justify-between bg-white border border-slate-200 p-3 rounded-xl gap-3">
              <div className="flex items-center gap-3">
                <span className={`px-2 py-1 rounded-md text-xs font-bold ${v.transmission === 'automatic' ? 'bg-violet-100 text-violet-700' : 'bg-amber-100 text-amber-700'}`}>
                  {v.transmission === 'automatic' ? 'أوتو' : 'يدوي'}
                </span>
                {v.color && <span className="text-sm font-bold text-slate-700">لون: {v.color}</span>}
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm font-black text-emerald-600">{v.total_price.toLocaleString()} دج</span>
                <button type="button" onClick={() => removeVariant(v.id)} className="text-red-500 hover:text-red-700 bg-red-50 p-1.5 rounded-lg">
                  <SafeIcon icon={LuTrash2} className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isAdding && (
        <div className="bg-white border border-indigo-200 rounded-xl p-4 shadow-sm">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">اللون</label>
              <input type="text" value={newVariant.color} onChange={e => setNewVariant({...newVariant, color: e.target.value})} className="w-full p-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-indigo-500" placeholder="مثال: أسود" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">ناقل الحركة</label>
              <select value={newVariant.transmission} onChange={e => setNewVariant({...newVariant, transmission: e.target.value})} className="w-full p-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-indigo-500 bg-white">
                <option value="automatic">أوتوماتيك</option>
                <option value="manual">يدوي</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">سعر السيارة</label>
              <input type="number" value={newVariant.price} onChange={e => setNewVariant({...newVariant, price: e.target.value})} className="w-full p-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">سعر الجمركة</label>
              <input type="number" value={newVariant.customs_price} onChange={e => setNewVariant({...newVariant, customs_price: e.target.value})} className="w-full p-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">سعر الشحن</label>
              <input type="number" value={newVariant.shipping_price} onChange={e => setNewVariant({...newVariant, shipping_price: e.target.value})} className="w-full p-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">السعر الإجمالي (دج)</label>
              <input type="number" value={newVariant.total_price} onChange={e => setNewVariant({...newVariant, total_price: e.target.value})} placeholder="تلقائي" className="w-full p-2 border border-emerald-300 bg-emerald-50 rounded-lg text-sm font-bold text-emerald-700 outline-none focus:border-emerald-500" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">عمولة مباشر</label>
              <input type="number" value={newVariant.commission} onChange={e => setNewVariant({...newVariant, commission: e.target.value})} className="w-full p-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">عمولة أب</label>
              <input type="number" value={newVariant.parent_commission} onChange={e => setNewVariant({...newVariant, parent_commission: e.target.value})} className="w-full p-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-indigo-500" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setIsAdding(false)} className="px-3 py-1.5 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg">إلغاء</button>
            <button type="button" onClick={handleAdd} className="px-4 py-1.5 text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg">حفظ النسخة</button>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminCarForm = ({ carToEdit, onClose, onSave }) => {
  const [formData, setFormData] = useState(initialForm);
  const [images, setImages] = useState([]);
  const [variants, setVariants] = useState([]); 
  const [showLibrary, setShowLibrary] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const [isCustomBrand, setIsCustomBrand] = useState(false);
  const [isCustomModel, setIsCustomModel] = useState(false);

  const calcData = useMemo(() => getCalculatorData(), []);
  const brands = useMemo(() => [...new Set(calcData.map(i => i.brand))].sort(), [calcData]);
  const availableModels = useMemo(() => {
    if (!formData.brand || isCustomBrand) return [];
    return [...new Set(calcData.filter(i => i.brand === formData.brand).map(i => i.model))].sort();
  }, [calcData, formData.brand, isCustomBrand]);

  const autoTotal = useMemo(() => {
    const p = Number(formData.price) || 0;
    const s = Number(formData.shipping_price) || 0;
    const c = Number(formData.customs_price) || 0;
    return p + s + c;
  }, [formData.price, formData.shipping_price, formData.customs_price]);

  useEffect(() => {
    if (carToEdit) {
      let endDate = '';
      if (carToEdit.discount_end_date) {
        endDate = new Date(carToEdit.discount_end_date).toISOString().slice(0, 16);
      }
      setFormData({
        ...carToEdit,
        status: carToEdit.status || 'available',
        condition: carToEdit.condition || 'used',
        transmission: carToEdit.transmission || 'manual',
        color: carToEdit.color || '',
        video_url: carToEdit.video_url || '',
        shipping_price: carToEdit.shipping_price || '',
        customs_price: carToEdit.customs_price || '',
        total_price: carToEdit.total_price || '',
        commission: carToEdit.commission || '',
        parent_commission: carToEdit.parent_commission || '',
        discount_enabled: carToEdit.discount_enabled || false,
        discount_price: carToEdit.discount_price || '',
        discount_end_date: endDate,
        discount_auto_repeat: carToEdit.discount_auto_repeat || false,
        discount_repeat_days: carToEdit.discount_repeat_days || 7,
      });

      const existingImages = [carToEdit.image_url, ...(carToEdit.gallery || [])].filter(Boolean);
      setImages(existingImages);
      
      if (carToEdit.variants && Array.isArray(carToEdit.variants)) {
        setVariants(carToEdit.variants);
      }

      if (!brands.includes(carToEdit.brand)) setIsCustomBrand(true);
    }
  }, [carToEdit, brands]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleBrandChange = (e) => {
    const val = e.target.value;
    if (val === '__OTHER__') {
      setIsCustomBrand(true);
      setFormData(prev => ({ ...prev, brand: '', model: '' }));
    } else {
      setIsCustomBrand(false);
      setIsCustomModel(false);
      setFormData(prev => ({ ...prev, brand: val, model: '' }));
    }
  };

  const handleModelChange = (e) => {
    const val = e.target.value;
    if (val === '__OTHER__') {
      setIsCustomModel(true);
      setFormData(prev => ({ ...prev, model: '' }));
    } else {
      setIsCustomModel(false);
      setFormData(prev => ({ ...prev, model: val }));
    }
  };

  const removeImage = (idx) => setImages(prev => prev.filter((_, i) => i !== idx));

  const handleAddExternal = () => {
    if (externalUrl) {
      setImages(prev => [...prev, externalUrl]);
      setExternalUrl('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isSupabaseConfigured) {
      alert('خطأ: لم يتم ربط Supabase.');
      return;
    }
    if (!formData.brand || !formData.model) {
      alert('يرجى إدخال الماركة والموديل');
      return;
    }

    setLoading(true);

    try {
      const finalImageUrl = images.length > 0 ? images[0] : '';
      const finalGallery = images.length > 1 ? images.slice(1) : [];

      const dataToSave = {
        ...formData,
        image_url: finalImageUrl,
        gallery: finalGallery,
        variants: variants,
        year: Number(formData.year),
        price: Number(formData.price) || 0,
        mileage: Number(formData.mileage) || 0,
        shipping_price: Number(formData.shipping_price) || 0,
        customs_price: Number(formData.customs_price) || 0,
        total_price: formData.total_price ? Number(formData.total_price) : autoTotal,
        commission: Number(formData.commission) || 0,
        parent_commission: Number(formData.parent_commission) || 0,
        discount_price: Number(formData.discount_price) || 0,
        discount_end_date: formData.discount_end_date ? new Date(formData.discount_end_date).toISOString() : null,
        discount_repeat_days: Number(formData.discount_repeat_days) || 7,
        whatsapp: formData.whatsapp || DEFAULT_WHATSAPP,
        video_url: formData.video_url || '',
      };

      if (carToEdit) {
        const { error } = await supabase.from('cars_for_sale').update(dataToSave).eq('id', carToEdit.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('cars_for_sale').insert([dataToSave]);
        if (error) throw error;
      }

      onSave();
    } catch (error) {
      alert(`حدث خطأ: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none";

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center p-6 border-b border-slate-100 sticky top-0 bg-white z-10">
            <h2 className="text-xl font-bold text-slate-800">{carToEdit ? 'تعديل سيارة' : 'إضافة سيارة جديدة'}</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 bg-slate-100 p-2 rounded-full">
              <SafeIcon icon={LuX} className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-3">حالة السيارة</label>
                <div className="grid grid-cols-2 gap-3">
                  {[{ val: 'new', label: 'جديدة', icon: LuSparkles, active: 'border-blue-500 bg-blue-50 text-blue-700' },
                    { val: 'used', label: 'مستعملة', icon: LuWrench, active: 'border-orange-400 bg-orange-50 text-orange-700' }].map(opt => (
                    <button key={opt.val} type="button" onClick={() => setFormData(prev => ({ ...prev, condition: opt.val }))} className={`flex items-center gap-2 p-3 rounded-xl border-2 font-medium text-sm transition-all ${formData.condition === opt.val ? opt.active : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'}`}>
                      <SafeIcon icon={opt.icon} className="w-4 h-4" /><span className="font-bold">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-3">حالة الإعلان</label>
                <div className="grid grid-cols-2 gap-3">
                  {[{ val: 'available', label: 'متوفرة للبيع', icon: LuClock, color: 'border-emerald-500 bg-emerald-50 text-emerald-700' },
                    { val: 'sold', label: 'تم البيع', icon: LuCheckCircle, color: 'border-red-400 bg-red-50 text-red-700' }].map(opt => (
                    <button key={opt.val} type="button" onClick={() => setFormData(prev => ({ ...prev, status: opt.val }))} className={`flex items-center gap-2 p-3 rounded-xl border-2 font-medium text-sm transition-all ${formData.status === opt.val ? opt.color : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'}`}>
                      <SafeIcon icon={opt.icon} className="w-4 h-4" />
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">الماركة</label>
                {!isCustomBrand ? (
                  <select value={formData.brand} onChange={handleBrandChange} required className={inputCls + ' bg-white cursor-pointer'}>
                    <option value="">-- اختر الماركة --</option>
                    {brands.map(b => <option key={b} value={b}>{b}</option>)}
                    <option value="__OTHER__">+ إضافة ماركة أخرى...</option>
                  </select>
                ) : (
                  <div className="flex gap-2">
                    <input required type="text" name="brand" value={formData.brand} onChange={handleChange} placeholder="اكتب الماركة..." className={inputCls} />
                    <button type="button" onClick={() => setIsCustomBrand(false)} className="px-3 bg-slate-100 text-slate-600 rounded-xl text-sm hover:bg-slate-200">إلغاء</button>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">الموديل</label>
                {!isCustomBrand && !isCustomModel ? (
                  <select value={formData.model} onChange={handleModelChange} disabled={!formData.brand} required className={inputCls + ' bg-white cursor-pointer disabled:bg-slate-100'}>
                    <option value="">-- اختر الموديل --</option>
                    {availableModels.map(m => <option key={m} value={m}>{m}</option>)}
                    <option value="__OTHER__">+ إضافة موديل آخر...</option>
                  </select>
                ) : (
                  <div className="flex gap-2">
                    <input required type="text" name="model" value={formData.model} onChange={handleChange} placeholder="اكتب الموديل..." className={inputCls} />
                    {!isCustomBrand && <button type="button" onClick={() => setIsCustomModel(false)} className="px-3 bg-slate-100 text-slate-600 rounded-xl text-sm hover:bg-slate-200">إلغاء</button>}
                  </div>
                )}
              </div>
              
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2 text-xs font-bold text-slate-500 mb-1">المواصفات الأساسية (للنسخة الافتراضية)</div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">ناقل الحركة</label>
                  <select name="transmission" value={formData.transmission} onChange={handleChange} className={inputCls + ' bg-white'}>
                    <option value="manual">يدوي (Manual)</option>
                    <option value="automatic">أوتوماتيك (Automatic)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">اللون</label>
                  <input type="text" name="color" value={formData.color} onChange={handleChange} placeholder="مثال: أبيض، أسود..." className={inputCls} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">السنة</label>
                <input required type="number" name="year" value={formData.year} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">الممشى (كم)</label>
                <input required type="number" name="mileage" value={formData.mileage} onChange={handleChange} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">رقم الواتساب</label>
                <input required type="text" name="whatsapp" value={formData.whatsapp} onChange={handleChange} dir="ltr" className={inputCls + ' text-left'} />
              </div>
            </div>

            <div className="bg-gradient-to-br from-slate-50 to-emerald-50/30 p-5 rounded-xl border border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={LuCalculator} className="w-4 h-4 text-emerald-600" />
                </div>
                <label className="text-sm font-bold text-slate-700">تفاصيل التسعير للنسخة الأساسية</label>
              </div>

              {(formData.brand || formData.model) && (
                <div className="mb-4">
                  <CustomsAutoFill brand={formData.brand} model={formData.model} year={formData.year} condition={formData.condition} onSelect={(val) => setFormData(prev => ({ ...prev, customs_price: val }))} />
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <PriceInput label="سعر السيارة" name="price" value={formData.price} onChange={handleChange} hint="السعر الأساسي للسيارة" />
                <div className="p-4 rounded-xl border-2 border-slate-200 bg-white">
                  <label className="block text-sm font-bold mb-1.5 text-slate-700">سعر الجمركة</label>
                  <div className="relative">
                    <input type="number" name="customs_price" value={formData.customs_price} onChange={handleChange} placeholder="0" className="w-full p-3 pe-12 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none" />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400">دج</span>
                  </div>
                </div>
                <PriceInput label="سعر الشحن" name="shipping_price" value={formData.shipping_price} onChange={handleChange} hint="تكلفة الشحن والنقل" />
                <div className="p-4 rounded-xl border-2 border-emerald-400 bg-emerald-50">
                  <label className="block text-sm font-bold text-emerald-700 mb-1.5">السعر الإجمالي</label>
                  <div className="bg-emerald-100/60 rounded-lg px-3 py-2 mb-2 flex items-center justify-between">
                    <span className="text-xs text-emerald-600 font-medium">الحساب التلقائي:</span>
                    <span className="text-emerald-700 font-black text-base">{autoTotal.toLocaleString()} دج</span>
                  </div>
                  <div className="relative">
                    <input type="number" name="total_price" value={formData.total_price} onChange={handleChange} placeholder={autoTotal > 0 ? autoTotal.toString() : "اتركه فارغاً"} className="w-full p-3 pe-12 border-2 border-emerald-300 rounded-xl focus:ring-2 focus:ring-emerald-400 outline-none font-black text-emerald-700 text-lg bg-white" />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-emerald-500">دج</span>
                  </div>
                </div>
              </div>

              {/* قسم العمولات */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl border-2 border-pink-300 bg-pink-50">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={LuHeartHandshake} className="w-5 h-5 text-pink-600" />
                    <label className="block text-sm font-black text-pink-700">عمولة المسوق المباشر</label>
                  </div>
                  <p className="text-[11px] text-pink-600/80 mb-3 leading-tight">الربح الذي يأخذه المسوق الذي أتى بالزبون الفعلي لهذه السيارة.</p>
                  <div className="relative">
                    <input type="number" name="commission" value={formData.commission} onChange={handleChange} placeholder="أدخل مبلغ الربح..." className="w-full p-3 pe-12 border-2 border-pink-200 rounded-xl focus:ring-2 focus:ring-pink-400 outline-none font-bold text-pink-700 text-lg bg-white" />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-pink-500">دج</span>
                  </div>
                </div>
                <div className="p-4 rounded-xl border-2 border-purple-300 bg-purple-50">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={LuUsers} className="w-5 h-5 text-purple-600" />
                    <label className="block text-sm font-black text-purple-700">عمولة المسوق الداعي (الأب)</label>
                  </div>
                  <p className="text-[11px] text-purple-600/80 mb-3 leading-tight">الربح الذي يأخذه المسوق الذي قام بدعوة المسوق المباشر للموقع.</p>
                  <div className="relative">
                    <input type="number" name="parent_commission" value={formData.parent_commission} onChange={handleChange} placeholder="أدخل مبلغ الربح..." className="w-full p-3 pe-12 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-400 outline-none font-bold text-purple-700 text-lg bg-white" />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-purple-500">دج</span>
                  </div>
                </div>
              </div>
            </div>

            {/* --- إدارة النسخ (Variants) --- */}
            <VariantManager variants={variants} setVariants={setVariants} baseData={formData} />

            {/* --- التخفيضات (التي كانت تسبب الخطأ) --- */}
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
              <label className="block text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <SafeIcon icon={LuTag} className="w-4 h-4 text-red-500" />
                عرض التخفيض مع عداد العد التنازلي (يطبق على السعر الأساسي)
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer mb-4">
                <input type="checkbox" name="discount_enabled" checked={formData.discount_enabled} onChange={handleChange} className="w-5 h-5 accent-emerald-600" />
                <span className="font-bold text-slate-700">تفعيل التخفيض لهذه السيارة</span>
              </label>
              
              {formData.discount_enabled && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white p-4 rounded-xl border border-red-200">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">سعر التخفيض (السعر النهائي المخفض)</label>
                    <input type="number" name="discount_price" value={formData.discount_price} onChange={handleChange} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">تاريخ انتهاء التخفيض</label>
                    <input type="datetime-local" name="discount_end_date" value={formData.discount_end_date} onChange={handleChange} className={inputCls} />
                  </div>
                  <div className="sm:col-span-2 flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" name="discount_auto_repeat" checked={formData.discount_auto_repeat} onChange={handleChange} className="w-4 h-4 accent-emerald-600" />
                      <span className="text-sm font-bold text-slate-700">تكرار العداد تلقائياً عند الانتهاء</span>
                    </label>
                    {formData.discount_auto_repeat && (
                      <div className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200">
                        <span className="text-xs text-slate-500 font-bold">كل</span>
                        <input type="number" name="discount_repeat_days" value={formData.discount_repeat_days} onChange={handleChange} className="w-16 p-1 border border-slate-300 rounded text-center text-sm outline-none font-bold" min="1" />
                        <span className="text-xs text-slate-500 font-bold">أيام</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-slate-50 p-5 rounded-xl border border-slate-200">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
                <label className="block text-sm font-bold text-slate-700 flex items-center gap-1.5">
                  <SafeIcon icon={LuImages} className="w-5 h-5 text-indigo-600" /> صور السيارة
                </label>
                <button type="button" onClick={() => setShowLibrary(true)} className="text-sm bg-indigo-600 text-white font-bold px-5 py-2.5 rounded-xl hover:bg-indigo-700 transition-colors shadow-sm w-full sm:w-auto">
                  + إضافة صور من المكتبة
                </button>
              </div>
              
              <div className="flex gap-2 mb-5">
                <input type="url" value={externalUrl} onChange={e => setExternalUrl(e.target.value)} placeholder="أو ألصق رابط صورة خارجي..." dir="ltr" className="flex-1 p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-left text-sm bg-white" onKeyDown={e => { if(e.key === 'Enter') { e.preventDefault(); handleAddExternal(); } }} />
                <button type="button" onClick={handleAddExternal} disabled={!externalUrl} className="bg-slate-200 hover:bg-slate-300 disabled:opacity-50 text-slate-700 font-bold px-5 rounded-xl text-sm transition-colors">إضافة</button>
              </div>

              {images.length > 0 ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {images.map((url, idx) => (
                    <div key={idx} className="relative group aspect-square rounded-xl border-2 border-slate-200 overflow-hidden bg-white shadow-sm">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      {idx === 0 && <span className="absolute top-2 right-2 bg-emerald-500 text-white text-[10px] font-black px-2 py-1 rounded shadow-md">رئيسية</span>}
                      <button type="button" onClick={() => removeImage(idx)} className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <SafeIcon icon={LuTrash2} className="w-8 h-8 text-white mb-1" />
                        <span className="text-white text-xs font-bold">إزالة</span>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10 border-2 border-dashed border-slate-300 rounded-xl bg-white">
                  <SafeIcon icon={LuImages} className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-500">لا توجد صور مضافة</p>
                </div>
              )}
            </div>

            {/* Video URL Input */}
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
              <label className="block text-sm font-bold text-slate-700 mb-1 flex items-center gap-1.5">
                <SafeIcon icon={LuVideo} className="w-4 h-4 text-slate-500" /> رابط يوتيوب أو فيديو للسيارة (اختياري)
              </label>
              <input type="text" name="video_url" value={formData.video_url} onChange={handleChange} className={inputCls} placeholder="رابط يوتيوب، تيك توك، أو ملف فيديو..." dir="ltr" />
              <p className="text-xs text-slate-500 mt-2">سيتم عرض الفيديو في صفحة تفاصيل السيارة للزوار.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">الوصف</label>
              <textarea name="description" value={formData.description} onChange={handleChange} rows="3" className={inputCls + ' resize-none'} />
            </div>

            <div className="pt-2">
              <button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-medium transition-colors flex justify-center items-center">
                {loading ? <div className="flex items-center"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin me-2"></div> جاري الحفظ...</div> : <><SafeIcon icon={LuSave} className="w-5 h-5 me-2" /> حفظ البيانات</>}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showLibrary && (
        <MediaLibrary onClose={() => setShowLibrary(false)} onSelect={(selectedUrls) => setImages(prev => [...prev, ...selectedUrls])} />
      )}
    </>
  );
};

export default AdminCarForm;